import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core';
import { Icon } from '@/components/ui/icon';
import { useEventCategoryLabel } from './categories';

export interface EventCategoryPillItem {
  value: string;
  count: number;
  /** True when the current date selection has no event in this category. */
  disabled: boolean;
}

export interface EventCategoryPillsProps {
  items: EventCategoryPillItem[];
  /** Selected calendar-category values (multi-select). */
  activeValues: string[];
  onToggle: (value: string) => void;
  /** Opens the request-private-event sheet. Shown to every caller (member or not) — private event requests aren't membership-gated. Optional only so callers that never offer it can omit it. */
  onAddEvent?: () => void;
  /** Shows the "Clear all" action in place of the pill list start when filters are active. */
  showClearAll?: boolean;
  onClearAll?: () => void;
}

function Pill({
  label,
  isActive,
  disabled,
  onClick,
}: {
  label: string;
  isActive: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        // Fixed `h-9` + `leading-none` rather than `py-2` around an inherited line-height. With
        // `line-height: normal` the text box is whatever the font's metrics produce — a fractional
        // height, which `items-center` then centres at a fractional y. On iOS (DPR 2/3) the 0.5px
        // hairline at that position straddles a device pixel and WebKit drops the bottom edge, so
        // the pill renders with its underside missing. Every other 0.5px-bordered control in the
        // app already pins an integer height (`h-10`, `h-11.5`, `size-5`) — this one didn't.
        'font-pp-neue-montreal flex h-9 shrink-0 items-center rounded-[20px] border border-solid px-4 text-[14px] leading-none tracking-[0.5px] whitespace-nowrap transition-colors',
        disabled
          ? 'border-jncquoi-neutral-200 text-jncquoi-neutral-500'
          : isActive
            ? 'bg-jncquoi-neutral-1000 text-jncquoi-neutral-00 border-transparent'
            : 'border-jncquoi-neutral-700 text-jncquoi-neutral-1000'
      )}
    >
      {label}
    </button>
  );
}

/**
 * Calendar-category filter pills — multi-select, faceted against the selected
 * dates (a pill with zero matching events is disabled). A pinned "add event"
 * (+) button and, when filters are active, a "Clear all" action sit to the
 * left of the scrolling pill list. Adapted from `ShopProductListTabBar`.
 */
export function EventCategoryPills({
  items,
  activeValues,
  onToggle,
  onAddEvent,
  showClearAll,
  onClearAll,
}: EventCategoryPillsProps) {
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();

  const showClear = showClearAll && !!onClearAll;

  return (
    <div className="flex w-full items-center">
      {(onAddEvent || showClear) && (
        <div className="flex shrink-0 items-center gap-2 py-4 pl-4">
          {onAddEvent && (
            <button
              type="button"
              onClick={onAddEvent}
              aria-label={t('App.Events.RequestPrivateEvent')}
              className="bg-jncquoi-neutral-1000 flex size-10 items-center justify-center rounded-full"
            >
              <Icon name="calendar-plus" width={24} height={24} className="text-jncquoi-neutral-00" />
            </button>
          )}
          {showClear && (
            <button
              type="button"
              onClick={onClearAll}
              className="font-pp-neue-montreal text-jncquoi-neutral-1000 shrink-0 px-2 text-xs font-normal tracking-wide whitespace-nowrap uppercase"
            >
              {t('App.Events.ClearAll')}
            </button>
          )}
        </div>
      )}
      {/* `overflow-y-hidden` is explicit: with only `overflow-x-auto`, `overflow-y` computes to
          `auto`, which lets iOS momentum-scroll the row by a sub-pixel. The row never scrolls
          vertically — same pairing as the shop tab bar (`shop/product/list/filters/tab-bar.tsx`). */}
      <div className="flex min-w-px flex-1 scrollbar-none items-center gap-4 overflow-x-auto overflow-y-hidden py-4 pr-4 pl-4">
        {items.map(item => {
          const isActive = activeValues.includes(item.value);
          const categoryLabel = getCategoryLabel(item.value);
          const label = item.count ? `${categoryLabel} (${item.count})` : categoryLabel;
          return (
            <Pill
              key={item.value}
              label={label}
              isActive={isActive}
              disabled={item.disabled && !isActive}
              onClick={() => onToggle(item.value)}
            />
          );
        })}
      </div>
    </div>
  );
}
